/**
 * quickTools.ts
 * 创作面板轻量 AI 工具：卡文急救 / 润色 / 章节摘要 / 灵感点子。
 *
 * 设计原则：
 * - 点击工具后进入对话模式，提示词作为首条消息自动发出（完整展示给用户）；
 * - 提示词只描述**任务目标与输出约束**，不指示具体工具用法——
 *   系统提示词已注入完整工具说明（query/execute/help），agent 会自主感知
 *   并决定如何获取数据、如何写入（摘要写入经 execute 走审批链）；
 * - 会话标题固定为「工具名 + YYYYMMDDHHmm」，不调用标题生成 AI。
 */

export type QuickToolKind = 'plot' | 'polish' | 'summary' | 'inspiration';

export type PolishMode = 'polish' | 'condense' | 'expand';

/** 工具名（前端展示与固定标题前缀） */
export const QUICK_TOOL_NAMES: Record<QuickToolKind, string> = {
  plot: '卡文急救',
  polish: '润色',
  summary: '章节摘要',
  inspiration: '灵感点子',
};

/** 会话标题：工具名称·章节名（无章节时省略章节名段）；同工具同章节重复使用时由调用方追加序号（2）（3） */
export function buildQuickToolTitle(kind: QuickToolKind, chapterTitle?: string): string {
  const chapterPart = chapterTitle ? `·${chapterTitle}` : '';
  return `${QUICK_TOOL_NAMES[kind]}${chapterPart}`;
}

/** 会话页展示的友好文本（完整提示词仍发给 AI；调试模式开启时展示完整提示词） */
export function buildQuickToolDisplay(kind: QuickToolKind, polishMode?: PolishMode): string {
  const desc: Record<QuickToolKind, string> = {
    plot: '🔧 卡文急救：请帮我规划接下来的剧情走向',
    polish: '🔧 润色：请处理我选中的文字',
    summary: '🔧 章节摘要：请总结本章并更新摘要',
    inspiration: '🔧 灵感点子：请给我一些剧情灵感',
  };
  if (kind === 'polish' && polishMode) {
    const modeLabel = { polish: '润色', condense: '精简', expand: '扩写' }[polishMode];
    return `🔧 润色（${modeLabel}）：请处理我选中的文字`;
  }
  return desc[kind];
}

/** 1. 卡文急救：给出 3 个可续写走向（数据获取交给 agent 自主使用工具） */
export function buildPlotPrompt(): string {
  return `我卡文了，需要剧情走向建议。

请给出 3 个可续写的情节走向：
1. 每个走向用一句话标题概括，后跟 2~3 句具体展开（冲突/人物反应/伏笔钩子）；
2. 走向之间要有明显差异（推进主线 / 人物支线 / 意外转折）；
3. 需要作品数据（大纲、当前章节内容等）时请自行查询，只给方向和思路，不要写正文。`;
}

/** 3. 润色 / 精简 / 扩写：只告知文字位置（章节 + 段落范围），由 AI 自主查询定位 */
export function buildPolishPrompt(opts: { mode: PolishMode; selection: { chapterTitle: string; chapterId: string; startLine: number; endLine: number } }): string {
  const { mode, selection } = opts;
  const modeDesc = {
    polish: '润色：修正语病、优化表达与节奏，保持原意和文风',
    condense: '精简：压缩冗余，保留核心信息，控制在原文约 60% 的篇幅',
    expand: '扩写：丰富细节与描写，篇幅约为原文的 1.5 倍，保持文风一致',
  }[mode];
  return `请对【引用的文字位置】所指的文字执行【${modeDesc}】。

【引用的文字位置】
- 章节：《${selection.chapterTitle}》（ID: ${selection.chapterId}）
- 段落范围：第 ${selection.startLine}-${selection.endLine} 段（正文以换行分行，段落号从 1 开始）

请查询该章节正文，按段落号定位文字后处理。只输出处理后的文字，不要任何解释、前缀或后缀。`;
}

/** 4. 章节摘要生成：生成摘要并更新到章节摘要字段（写入走 execute 审批链，由 agent 自主调用） */
export function buildSummaryPrompt(): string {
  return `请为当前查看的章节生成摘要：
- 80~150 字，概括本章情节进展与关键信息（人物动向、冲突、伏笔），
- 客观叙述，不含评价与剧透警告，不要出现"本章""本回"字样。

需要阅读章节正文时请自行查询；生成摘要后请将其更新到该章节的摘要字段（更新作品数据时会弹出审批，请等待用户确认）。

只输出摘要正文。`;
}

/** 5. 灵感生成器：给出 5 个灵感点子（数据获取交给 agent 自主使用工具） */
export function buildInspirationPrompt(): string {
  return `请给我 5 个可用于后续剧情的灵感点子：
- 覆盖不同类型：情节冲突、意外转折、人物动机、世界观拓展、情感线推进；
- 每条一行，用"点子：一句话标题 —— 具体构思（20~40 字）"的格式；
- 需要了解作品大纲等背景时请自行查询，要贴合现有大纲基调，不要写正文。`;
}
